import { FC, useMemo, useState, memo, useEffect } from "react"
import { PaginationState } from "@tanstack/react-table"
import { If, ListSkeleton, Table } from "../../../ui"
import { IntegrationCell } from "./cells"
import BrandAutocomplete from "../../brand-autocomplete"
import {
  Integration,
  BBColumnDef,
  Brand,
  IntegrationType
} from "../../../../types"

interface TabDef {
  label: string
  type: IntegrationType | "all"
}

const TABS: TabDef[] = [
  { label: "All", type: "all" },
  { label: "Standalone", type: "standalone" },
  { label: "API", type: "api" },
  { label: "Loan Officer Page", type: "boomtown" },
  { label: "Website Chat", type: "chatbot" }
]

interface Props {
  todayIntegrations?: Integration[]
  /** Full (unsliced) history list — filtering and pagination happen client-side */
  integrations?: Integration[]
  brands?: Brand[]
  selectedBrandId: string
  onSelectedBrandChange: (brandId: string) => void
  isLoading: boolean
  onSelect?: (id: string) => void
}

const PAGE_SIZE = 10

function matchesTab(
  integration: Integration,
  type: IntegrationType | "all"
): boolean {
  if (type === "all") return true
  return integration.type === type
}

function timestampToDate(created: string): Date {
  return new Date(parseInt(created, 10) * 1000)
}

function formatDateHeader(created: string): string {
  const date = timestampToDate(created)
  if (date.toDateString() === new Date().toDateString()) return "TODAY"
  return date
    .toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric"
    })
    .toUpperCase()
}

function groupByDate(
  integrations: Integration[]
): { label: string; items: Integration[] }[] {
  const groups: Map<string, Integration[]> = new Map()

  for (const integration of integrations) {
    const dateKey = integration.created
      ? timestampToDate(integration.created).toDateString()
      : "Unknown"
    if (!groups.has(dateKey)) groups.set(dateKey, [])
    groups.get(dateKey)!.push(integration)
  }

  return Array.from(groups.entries()).map(([, items]) => ({
    label: items[0].created
      ? formatDateHeader(items[0].created)
      : "UNKNOWN DATE",
    items
  }))
}

const IntegrationsTable: FC<Props> = memo(
  ({
    todayIntegrations = [],
    integrations = [],
    brands = [],
    selectedBrandId,
    onSelectedBrandChange,
    isLoading,
    onSelect
  }) => {
    const [activeTab, setActiveTab] = useState<IntegrationType | "all">("all")
    const [pagination, setPagination] = useState<PaginationState>({
      pageIndex: 0,
      pageSize: PAGE_SIZE
    })

    // Reset to first page when brand changes
    useEffect(() => {
      setPagination(p => ({ ...p, pageIndex: 0 }))
    }, [selectedBrandId])

    const filteredToday = useMemo(
      () => todayIntegrations.filter(i => matchesTab(i, activeTab)),
      [todayIntegrations, activeTab]
    )

    // Apply tab filter to full history list first, then paginate
    const filteredHistory = useMemo(
      () => integrations.filter(i => matchesTab(i, activeTab)),
      [integrations, activeTab]
    )

    const totalCount = filteredHistory.length

    const pageCount = useMemo(
      () => Math.ceil(totalCount / pagination.pageSize) || 1,
      [totalCount, pagination.pageSize]
    )

    const pagedHistory = useMemo(() => {
      const start = pagination.pageIndex * pagination.pageSize
      return filteredHistory.slice(start, start + pagination.pageSize)
    }, [filteredHistory, pagination.pageIndex, pagination.pageSize])

    const historyGroups = useMemo(
      () => groupByDate(pagedHistory),
      [pagedHistory]
    )

    const handleTabChange = (type: IntegrationType | "all") => {
      setActiveTab(type)
      setPagination(p => ({ ...p, pageIndex: 0 }))
    }

    const columns: BBColumnDef<Integration>[] = useMemo(
      () => [
        {
          id: "title",
          header: () => <>Name</>,
          footer: props => props.column.id,
          cell: info => (
            <IntegrationCell data={info.row.original} onSelect={onSelect} />
          )
        }
      ],
      [onSelect]
    )

    return (
      <div className="mt-6">
        <If
          condition={isLoading}
          then={<ListSkeleton />}
          else={
            <>
              {/* Toolbar */}
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  {TABS.map(tab => (
                    <button
                      key={tab.type}
                      type="button"
                      onClick={() => handleTabChange(tab.type)}
                      className={[
                        "rounded-full px-4 py-1.5 text-sm border transition-colors",
                        activeTab === tab.type
                          ? "bg-[#f4effe] border-[#4a1a9d] text-[#4a1a9d] font-medium"
                          : "bg-white border-[#e4e4e4] text-[#232227] font-normal"
                      ].join(" ")}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                {brands.length > 0 && (
                  <BrandAutocomplete
                    hideLabel
                    brands={brands}
                    selected={selectedBrandId}
                    onChange={onSelectedBrandChange}
                  />
                )}
              </div>

              {/* Date-grouped rows */}
              <div className="mt-8">
                {/* TODAY — always pinned at top */}
                {filteredToday.length > 0 && (
                  <div>
                    <div className="flex items-center gap-3 mb-2 mt-4">
                      <span className="text-xs font-semibold uppercase tracking-widest text-[#757575] whitespace-nowrap">
                        TODAY
                      </span>
                      <div className="flex-1 h-px bg-[#e4e4e4]" />
                    </div>
                    <Table
                      data={filteredToday}
                      columns={columns}
                      viewType="table"
                      pageSize={Math.max(filteredToday.length, 1)}
                      pageCount={1}
                      searchBarVisible={false}
                      noHeader
                      removeMobilePagination
                    />
                  </div>
                )}

                {/* Historical — client-paginated, grouped by date */}
                {historyGroups.map(group => (
                  <div key={group.label}>
                    <div className="flex items-center gap-3 mb-2 mt-4">
                      <span className="text-xs font-semibold uppercase tracking-widest text-[#757575] whitespace-nowrap">
                        {group.label}
                      </span>
                      <div className="flex-1 h-px bg-[#e4e4e4]" />
                    </div>
                    <Table
                      data={group.items}
                      columns={columns}
                      viewType="table"
                      pageSize={Math.max(group.items.length, 1)}
                      pageCount={1}
                      searchBarVisible={false}
                      noHeader
                      removeMobilePagination
                    />
                  </div>
                ))}

                {/* Pagination controls */}
                {pageCount > 1 && (
                  <Table
                    data={[]}
                    columns={columns}
                    viewType="table"
                    pageSize={pagination.pageSize}
                    pageCount={pageCount}
                    pagination={pagination}
                    setPagination={setPagination}
                    rowCount={totalCount}
                    searchBarVisible={false}
                    noHeader
                    removeMobilePagination
                    noDataElement={<></>}
                  />
                )}
              </div>
            </>
          }
        />
      </div>
    )
  }
)

IntegrationsTable.displayName = "IntegrationsTable"

export default IntegrationsTable
